/**
 * Admin — Health/Overview (Wave 1b W1b-17)
 *
 * The regulator-screenshotable page. Three sections:
 *   1. Status banner (green/amber/red) + Refresh button (POST /probe)
 *   2. KPI strip: 5 cards (total / healthy / degraded / down /
 *                  air-gap blocks 24h)
 *   3. Service grid: 13 cards (3 cols x 5 rows)
 *
 * GPU + host snapshot panels ship with W1b-19 (sibling worktree); v1
 * shows placeholder cards so the layout slot stays reserved.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, Wrench,
  Cpu, Server, ShieldOff, RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/format';
import { apiClient } from '@/lib/api-client';
import { queryClient } from '@/lib/query-client';

export const Route = createFileRoute('/admin/health/')({
  component: HealthOverviewPage,
});

// ─── Types ──────────────────────────────────────────────────────────────

type ServiceStatus = 'healthy' | 'degraded' | 'down' | 'unknown';

interface HealthOverviewService {
  name: string;
  status: ServiceStatus;
  latencyMs: number | null;
  lastProbedAt: string | null;
  version: string | null;
  inMaintenance: boolean;
  maintenanceReason: string | null;
}

interface HealthOverview {
  overall: 'healthy' | 'degraded' | 'down';
  services: HealthOverviewService[];
  gpu: { available: boolean; note: string };
  host: { available: boolean; note: string };
  kpis: {
    totalServices: number;
    healthyServices: number;
    degradedServices: number;
    downServices: number;
    airGapBlocksLast24h: number;
  };
}

// Round-4 admin audit (PR #482) P2-1 — Real-time block events (UI-SPEC
// §6.1). Sourced from the W1b-21 egress observer.
interface AirgapRecentAttempt {
  id: string;
  destination: string;
  port: number | null;
  protocol: string | null;
  process: string | null;
  reason: string | null;
  blockedAt: string;
  egressPolicyDecision: string;
  isTest: boolean;
}

interface AirgapStatus {
  counters: {
    blocksLast24h: number;
    totalBlocks: number;
    testBlocksLast24h: number;
    loggedOnlyLast24h: number;
  };
  allowlist: {
    activeCount: number;
    totalCount: number;
  };
  observer: {
    status: 'healthy' | 'degraded' | 'down';
    lastRunAt: string | null;
    ageMs: number | null;
    heartbeatKey: string;
    cadenceMs: number;
  };
  recentAttempts: AirgapRecentAttempt[];
}

// ─── Page ───────────────────────────────────────────────────────────────

function HealthOverviewPage() {
  const [refreshing, setRefreshing] = useState(false);

  const { data, isLoading, error } = useQuery<HealthOverview>({
    queryKey: ['admin', 'health', 'overview'],
    queryFn: () => apiClient.get<HealthOverview>('/admin/health'),
    refetchInterval: 30_000, // Auto-refresh every 30s
  });

  // Round-4 admin audit (PR #482) P2-1 — air-gap status + recentAttempts
  // for the air-gap enforcement card. Refetches on the same 30s cadence.
  const { data: airgap } = useQuery<AirgapStatus>({
    queryKey: ['admin', 'health', 'airgap'],
    queryFn: () => apiClient.get<AirgapStatus>('/admin/health/airgap'),
    refetchInterval: 30_000,
  });

  const probeMutation = useMutation({
    mutationFn: () => apiClient.post('/admin/health/probe', {}),
    onMutate: () => setRefreshing(true),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['admin', 'health', 'overview'],
      });
      toast.success('Health probe complete');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Probe failed';
      toast.error(`Probe failed: ${message}`);
    },
    onSettled: () => setRefreshing(false),
  });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-sm text-muted-foreground">Loading health overview...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold">Could not load health data</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {error instanceof Error ? error.message : 'Unknown error'}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const overallStatus = data.overall;

  // Pick the oldest probedAt across all services so a single stale row
  // does not hide behind a fresh one.
  const oldestProbedAt = data.services.reduce<string | null>((oldest, s) => {
    if (!s.lastProbedAt) return oldest;
    if (!oldest) return s.lastProbedAt;
    return new Date(s.lastProbedAt).getTime() < new Date(oldest).getTime()
      ? s.lastProbedAt
      : oldest;
  }, null);

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-5" data-testid="health-overview">
      {/* Status banner */}
      <div
        className={cn(
          'border rounded-lg p-4 flex items-center gap-4',
          overallStatus === 'healthy' && 'bg-emerald-500/5 border-emerald-500/30',
          overallStatus === 'degraded' && 'bg-amber-500/5 border-amber-500/30',
          overallStatus === 'down' && 'bg-red-500/5 border-red-500/30',
        )}
        data-testid="health-banner"
        data-overall-status={overallStatus}
      >
        <div
          className={cn(
            'h-12 w-12 rounded-full flex items-center justify-center shrink-0',
            overallStatus === 'healthy' && 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
            overallStatus === 'degraded' && 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
            overallStatus === 'down' && 'bg-red-500/15 text-red-600 dark:text-red-400',
          )}
        >
          {overallStatus === 'healthy' ? <CheckCircle2 className="h-6 w-6" />
            : overallStatus === 'degraded' ? <AlertTriangle className="h-6 w-6" />
            : <XCircle className="h-6 w-6" />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-semibold">
            {overallStatus === 'healthy' && 'All systems operational'}
            {overallStatus === 'degraded' && 'Degraded performance detected'}
            {overallStatus === 'down' && 'Service outage detected'}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {data.kpis.healthyServices} healthy, {data.kpis.degradedServices} degraded, {data.kpis.downServices} down.
            {oldestProbedAt && ` Oldest probe ${relativeTime(oldestProbedAt)}.`}
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 text-xs gap-1.5"
          onClick={() => probeMutation.mutate()}
          disabled={refreshing || probeMutation.isPending}
          title="Run health probe across all 13 services"
          data-testid="health-refresh"
        >
          <RefreshCw className={cn('h-3 w-3', (refreshing || probeMutation.isPending) && 'animate-spin')} />
          {(refreshing || probeMutation.isPending) ? 'Probing...' : 'Refresh'}
        </Button>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-5 gap-3" data-testid="health-kpis">
        <KpiCard
          label="Total"
          value={data.kpis.totalServices}
          tone="neutral"
        />
        <KpiCard
          label="Healthy"
          value={data.kpis.healthyServices}
          tone="success"
        />
        <KpiCard
          label="Degraded"
          value={data.kpis.degradedServices}
          tone="warning"
        />
        <KpiCard
          label="Down"
          value={data.kpis.downServices}
          tone="danger"
        />
        <KpiCard
          label="Egress blocks (24h)"
          value={data.kpis.airGapBlocksLast24h}
          tone="success"
          mono
          testId="kpi-airgap"
        />
      </div>

      {/* Service grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold">Services ({data.kpis.totalServices})</h2>
          <Link to="/admin/health/services" className="text-[10px] text-primary hover:underline">
            Detailed view →
          </Link>
        </div>
        <div className="grid grid-cols-3 gap-3" data-testid="service-grid">
          {data.services.map((s) => (
            <ServiceCard key={s.name} service={s} />
          ))}
        </div>
      </div>

      {/* Air-gap egress + GPU placeholder */}
      <div className="grid grid-cols-2 gap-5">
        <Card className="border-l-4 border-l-emerald-500" data-testid="airgap-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="h-10 w-10 rounded bg-emerald-500/10 flex items-center justify-center shrink-0">
                <ShieldOff className="h-5 w-5 text-emerald-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">Air-gap enforcement</div>
                <div className="text-3xl font-bold tabular-nums mt-0.5" data-testid="airgap-counter">
                  {data.kpis.airGapBlocksLast24h.toLocaleString()}
                </div>
                <div className="text-xs text-muted-foreground">
                  outbound attempts blocked (24h)
                </div>
              </div>
            </div>
            {/* Round-4 admin audit (PR #482) P2-1 — bind allowlist count
               + observer status to backend (was: hardcoded "empty" / "Real-
               time block events ship with W1b-21"). */}
            <div className="mt-3 pt-3 border-t text-[10px] text-muted-foreground">
              Egress allow-list:{' '}
              <span className="font-mono text-foreground" data-testid="airgap-allowlist-count">
                {airgap
                  ? airgap.allowlist.activeCount === 0
                    ? airgap.allowlist.totalCount === 0
                      ? 'empty'
                      : `${airgap.allowlist.totalCount} (none active)`
                    : `${airgap.allowlist.activeCount} active${airgap.allowlist.totalCount !== airgap.allowlist.activeCount ? ` of ${airgap.allowlist.totalCount}` : ''}`
                  : '…'}
              </span>
              . Network namespace:{' '}
              <span className="font-mono text-foreground">internal (no egress)</span>
              . Observer:{' '}
              <span
                className={cn(
                  'font-mono',
                  airgap?.observer.status === 'healthy' && 'text-emerald-600 dark:text-emerald-400',
                  airgap?.observer.status === 'degraded' && 'text-amber-600 dark:text-amber-400',
                  airgap?.observer.status === 'down' && 'text-red-600 dark:text-red-400',
                  !airgap && 'text-foreground',
                )}
                data-testid="airgap-observer-status"
              >
                {airgap?.observer.status ?? '…'}
              </span>
              .
            </div>
            {/* Recent attempts (UI-SPEC §6.1 — first 4 of OUTBOUND_ATTEMPTS). */}
            {airgap && airgap.recentAttempts.length > 0 && (
              <div className="mt-3 pt-3 border-t space-y-1" data-testid="airgap-recent-attempts">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide mb-1">
                  Recent attempts
                </div>
                {airgap.recentAttempts.slice(0, 4).map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center gap-2 text-[10px] text-muted-foreground"
                    data-testid="airgap-recent-attempt-row"
                  >
                    <ShieldOff className="h-2.5 w-2.5 text-red-500 shrink-0" />
                    <span className="font-mono text-foreground truncate flex-1 min-w-0">
                      {a.destination}
                      {a.port !== null ? `:${a.port}` : ''}
                    </span>
                    {a.isTest && (
                      <span className="px-1 py-0.5 rounded text-[9px] bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        Test
                      </span>
                    )}
                    <span className="tabular-nums shrink-0">
                      {relativeTime(a.blockedAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="gpu-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3 mb-3">
              <div className="h-10 w-10 rounded bg-purple-500/10 flex items-center justify-center shrink-0">
                <Cpu className="h-5 w-5 text-purple-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-muted-foreground uppercase tracking-wide">GPU snapshot</div>
                <div className="text-sm font-semibold">Coming in W1b-19</div>
                <div className="text-[10px] text-muted-foreground">{data.gpu.note}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t flex items-center gap-2 text-[10px] text-muted-foreground">
              <Server className="h-3 w-3" />
              Host snapshot also ships with W1b-19.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────

function KpiCard({
  label, value, tone, mono, testId,
}: {
  label: string;
  value: number;
  tone: 'neutral' | 'success' | 'warning' | 'danger';
  mono?: boolean;
  testId?: string;
}) {
  const accent =
    tone === 'success' ? 'text-emerald-600 dark:text-emerald-400'
    : tone === 'warning' ? 'text-amber-600 dark:text-amber-400'
    : tone === 'danger' ? 'text-red-600 dark:text-red-400'
    : 'text-foreground';
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3">
        <div className="text-[10px] text-muted-foreground uppercase tracking-wide">
          {label}
        </div>
        <div className={cn('text-2xl font-bold mt-1', accent, mono && 'tabular-nums')}>
          {value.toLocaleString()}
        </div>
      </CardContent>
    </Card>
  );
}

function ServiceCard({ service }: { service: HealthOverviewService }) {
  const status = service.inMaintenance ? 'maintenance' : service.status;
  const Icon =
    status === 'healthy' ? CheckCircle2
    : status === 'degraded' ? AlertTriangle
    : status === 'down' ? XCircle
    : status === 'maintenance' ? Wrench
    : Activity;
  const tone =
    status === 'healthy' ? 'border-emerald-500/30 bg-emerald-500/5'
    : status === 'degraded' ? 'border-amber-500/30 bg-amber-500/5'
    : status === 'down' ? 'border-red-500/30 bg-red-500/5'
    : status === 'maintenance' ? 'border-blue-500/30 bg-blue-500/5'
    : 'border-slate-300 bg-muted/30';
  const iconColor =
    status === 'healthy' ? 'text-emerald-500'
    : status === 'degraded' ? 'text-amber-500'
    : status === 'down' ? 'text-red-500'
    : status === 'maintenance' ? 'text-blue-500'
    : 'text-muted-foreground';

  return (
    <Link
      to="/admin/health/services"
      search={{ name: service.name } as never}
      className={cn(
        'border rounded-md p-3 block transition-colors hover:bg-muted/30',
        tone,
      )}
      data-testid="service-card"
      data-service-name={service.name}
      data-service-status={status}
      title={
        service.inMaintenance && service.maintenanceReason
          ? `In maintenance: ${service.maintenanceReason}`
          : `Click for ${service.name} details`
      }
    >
      <div className="flex items-start gap-2">
        <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', iconColor)} />
        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium font-mono truncate">{service.name}</div>
          <div className="text-[10px] text-muted-foreground">
            {service.version ? `v${service.version}` : 'no version'}
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px]">
        <span className="text-muted-foreground capitalize">{status}</span>
        <span className="tabular-nums">
          {service.latencyMs != null ? `${service.latencyMs}ms` : '—'}
        </span>
      </div>
    </Link>
  );
}
